import { useState } from 'react';
import type { UseMutationResult } from '@tanstack/react-query';
import { Plus, Tag } from 'lucide-react';
import { Button, Dialog, Input, Select } from '@/design-system';
import { Table, TableCell, TableHead, TableHeader, TableRow } from '@/design-system/Table';
import type { CreateCouponData } from '@/features/coupons';
import { formatCentsToBrl, parseBrlInputToCents, strategyLabel, valueLabel } from '@/features/coupons';
import { DURATION_TYPE_LABELS, TRIAL_DAYS_OPTIONS } from '@/features/coupons/utils/coupon-form';
import type { Affiliate } from '../api/affiliates.service';
import { useAffiliateCouponsQuery } from '../hooks/useAffiliateCouponsQuery';

const emptyAffiliateCouponForm = (): CreateCouponData => ({
  code: '',
  discountStrategy: 'PERCENTAGE',
  value: 10,
  durationType: 'FOREVER',
  usageType: 'AFFILIATE',
  description: '',
});

interface AffiliateCouponsDialogProps {
  isOpen: boolean;
  affiliate: Affiliate | null;
  onClose: () => void;
  createAffiliateCoupon: UseMutationResult<
    unknown,
    unknown,
    { affiliateId: string; formData: CreateCouponData },
    unknown
  >;
}

export function AffiliateCouponsDialog({
  isOpen,
  affiliate,
  onClose,
  createAffiliateCoupon,
}: AffiliateCouponsDialogProps) {
  const [showCreateCoupon, setShowCreateCoupon] = useState(false);
  const [couponFormData, setCouponFormData] = useState<CreateCouponData>(emptyAffiliateCouponForm());
  const [couponFormErrors, setCouponFormErrors] = useState<Record<string, string>>({});

  const { data: affiliateCoupons = [], isLoading } = useAffiliateCouponsQuery(affiliate?.id, isOpen);

  const handleDialogClose = () => {
    setShowCreateCoupon(false);
    setCouponFormData(emptyAffiliateCouponForm());
    setCouponFormErrors({});
    onClose();
  };

  const validateCouponForm = () => {
    const errors: Record<string, string> = {};
    const code = couponFormData.code.trim().toUpperCase();
    if (!code) errors.code = 'Código é obrigatório';
    else if (code.length < 2) errors.code = 'Código deve ter no mínimo 2 caracteres';
    if (couponFormData.discountStrategy === 'PERCENTAGE' && (couponFormData.value < 0 || couponFormData.value > 100)) {
      errors.value = 'Desconto deve ser entre 0 e 100%';
    }
    setCouponFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateCoupon = async () => {
    if (!validateCouponForm() || !affiliate) return;
    try {
      await createAffiliateCoupon.mutateAsync({ affiliateId: affiliate.id, formData: couponFormData });
      setShowCreateCoupon(false);
      setCouponFormData(emptyAffiliateCouponForm());
      setCouponFormErrors({});
    } catch {
      /* toast in mutation */
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleDialogClose}
      title={affiliate ? `Cupons de ${affiliate.name}` : 'Cupons'}
      size="lg"
      footer={showCreateCoupon ? (
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowCreateCoupon(false)} disabled={createAffiliateCoupon.isPending}>
            Cancelar
          </Button>
          <Button variant="primary" size="sm" onClick={() => void handleCreateCoupon()} isLoading={createAffiliateCoupon.isPending}>
            Criar cupom
          </Button>
        </div>
      ) : undefined}
    >
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <p className="text-sm text-gray-600">Cupons vinculados a este afiliado.</p>
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              setCouponFormData(emptyAffiliateCouponForm());
              setCouponFormErrors({});
              setShowCreateCoupon(true);
            }}
          >
            <Plus size={16} className="mr-1" /> Novo cupom
          </Button>
        </div>
        {isLoading ? (
          <p className="py-6 text-center text-gray-500">Carregando…</p>
        ) : affiliateCoupons.length === 0 && !showCreateCoupon ? (
          <div className="py-8 text-center">
            <Tag className="mx-auto h-10 w-10 text-gray-400" />
            <p className="mt-2 text-gray-500">Nenhum cupom cadastrado.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Código</TableHead>
                <TableHead>Benefício</TableHead>
                <TableHead>Valor</TableHead>
                <TableHead>Resgates</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <tbody className="bg-white divide-y divide-gray-200">
              {affiliateCoupons.map((c) => (
                <TableRow key={c.id}>
                  <TableCell>
                    <span className="font-mono font-medium">{c.code}</span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-gray-600">{strategyLabel(c)}</span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm font-medium">{valueLabel(c)}</span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">
                      {c.redeemedCount}
                      {c.maxRedemptions != null ? ` / ${c.maxRedemptions}` : ''}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full ${
                        c.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {c.isActive ? 'Ativo' : 'Inativo'}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </tbody>
          </Table>
        )}
        {showCreateCoupon && (
          <div className="border-t pt-4 space-y-4">
            <h4 className="font-medium">Novo cupom para este afiliado</h4>
            <Input
              label="Código do cupom"
              value={couponFormData.code}
              onChange={(e) => setCouponFormData((prev) => ({ ...prev, code: e.target.value.toUpperCase() }))}
              error={couponFormErrors.code}
              placeholder="Ex: JOAO10"
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Tipo de benefício</label>
              <Select
                value={couponFormData.discountStrategy}
                onChange={(e) => {
                  const v = e.target.value as CreateCouponData['discountStrategy'];
                  setCouponFormData((prev) => ({
                    ...prev,
                    discountStrategy: v,
                    value: v === 'TRIAL_DAYS' ? 30 : v === 'PERCENTAGE' ? 10 : 5000,
                  }));
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="TRIAL_DAYS">Dias grátis</option>
                <option value="PERCENTAGE">Desconto em porcentagem</option>
                <option value="FIXED_AMOUNT">Desconto em valor fixo (R$)</option>
              </Select>
            </div>
            {couponFormData.discountStrategy === 'TRIAL_DAYS' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Período</label>
                <Select
                  value={couponFormData.value}
                  onChange={(e) =>
                    setCouponFormData((prev) => ({ ...prev, value: parseInt(e.target.value, 10) as 7 | 15 | 30 }))
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {TRIAL_DAYS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Select>
              </div>
            )}
            {couponFormData.discountStrategy === 'PERCENTAGE' && (
              <Input
                label="Porcentagem"
                type="number"
                min={0}
                max={100}
                value={String(couponFormData.value)}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  setCouponFormData((prev) => ({
                    ...prev,
                    value: Number.isNaN(v) ? 0 : Math.min(100, Math.max(0, v)),
                  }));
                }}
                error={couponFormErrors.value}
              />
            )}
            {couponFormData.discountStrategy === 'FIXED_AMOUNT' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Valor (R$)</label>
                <Input
                  type="text"
                  inputMode="numeric"
                  value={couponFormData.value === 0 ? '' : `R$ ${formatCentsToBrl(couponFormData.value)}`}
                  onChange={(e) =>
                    setCouponFormData((prev) => ({ ...prev, value: parseBrlInputToCents(e.target.value) }))
                  }
                  placeholder="R$ 0,00"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            )}
            {(couponFormData.discountStrategy === 'PERCENTAGE' ||
              couponFormData.discountStrategy === 'FIXED_AMOUNT') && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Duração</label>
                  <Select
                    value={couponFormData.durationType}
                    onChange={(e) =>
                      setCouponFormData((prev) => ({
                        ...prev,
                        durationType: e.target.value as CreateCouponData['durationType'],
                      }))
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="ONCE">{DURATION_TYPE_LABELS.ONCE}</option>
                    <option value="REPEATING">{DURATION_TYPE_LABELS.REPEATING}</option>
                    <option value="FOREVER">{DURATION_TYPE_LABELS.FOREVER}</option>
                  </Select>
                </div>
                {couponFormData.durationType === 'REPEATING' && (
                  <Input
                    label="Quantidade de meses"
                    type="number"
                    min={1}
                    max={120}
                    value={String(couponFormData.durationMonths ?? 3)}
                    onChange={(e) =>
                      setCouponFormData((prev) => ({
                        ...prev,
                        durationMonths: parseInt(e.target.value, 10) || 1,
                      }))
                    }
                  />
                )}
              </>
            )}
            <Input
              label="Descrição (opcional)"
              value={couponFormData.description || ''}
              onChange={(e) => setCouponFormData((prev) => ({ ...prev, description: e.target.value }))}
            />
          </div>
        )}
      </div>
    </Dialog>
  );
}
